import type { FC, ReactNode } from 'react';
import React, { createContext, useMemo, useCallback, useState } from 'react';
import { graphql } from 'react-relay';

import type { useAudienceSegmentationProps } from '@atlassian/company-hub-common';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop';

import type {
	AudienceSegmentationContextProviderQuery$data,
	AudienceSegmentationContextProviderQuery$variables,
} from './__generated__/AudienceSegmentationContextProviderQuery.graphql';

const AudienceSegmentationContextProviderQuery = graphql`
	query AudienceSegmentationContextProviderQuery($accountId: ID) {
		user: confluence_atlassianUser(id: $accountId) {
			id
			confluence {
				groupsWithId {
					id
				}
			}
		}
	}
`;

export type AudienceSegmentationProviderProps = {
	initialAudienceGroupIds?: string[];
	initialSegmentationApplied?: boolean;
	shouldFetchUserGroups?: boolean;
	children?: ReactNode;
} & Pick<useAudienceSegmentationProps, 'isCompanyHub'>;

export type AudienceSegmentationContextValueType = {
	currentAudienceGroupIds: string[];
	setCurrentAudienceGroupIds: (groupIds: string[]) => void;
	isSegmentationApplied: boolean;
	setIsSegmentationApplied: (isSegmentationApplied: boolean) => void;
} & Pick<useAudienceSegmentationProps, 'isCompanyHub' | 'isTargetAudience'>;

export const COMMON_VIEW_ID = 'accessible-to-anyone';

export const AudienceSegmentationContext = createContext<AudienceSegmentationContextValueType>({
	isCompanyHub: false,
	currentAudienceGroupIds: [],
	setCurrentAudienceGroupIds: () => {},
	isTargetAudience: () => true,
	isSegmentationApplied: false,
	setIsSegmentationApplied: () => {},
});

const AudienceSegmentationContextProviderInternal: FC<AudienceSegmentationProviderProps> = ({
	children,
	isCompanyHub,
	initialAudienceGroupIds = [],
	initialSegmentationApplied = false,
	shouldFetchUserGroups = false,
}) => {
	const [currentAudienceGroupIds, setCurrentAudienceGroupIds] = useState(initialAudienceGroupIds);
	const [isSegmentationApplied, setIsSegmentationApplied] = useState(initialSegmentationApplied);

	const isTargetAudience = useCallback(
		(targetGroupIds: string[] | undefined) => {
			if (
				!isCompanyHub || // if we are outside of company hub, no filtering
				targetGroupIds === undefined // if no target groups are provided, no filtering
			) {
				return true;
			}
			setIsSegmentationApplied(true);
			const audienceGroupIdsSet = new Set(currentAudienceGroupIds);
			if (audienceGroupIdsSet.has(COMMON_VIEW_ID)) {
				return false;
			}
			return targetGroupIds?.some((allowedGroup) => audienceGroupIdsSet.has(allowedGroup));
		},
		[currentAudienceGroupIds, isCompanyHub],
	);

	const audienceSegmentationContextValue = useMemo(
		() => ({
			currentAudienceGroupIds,
			setCurrentAudienceGroupIds,
			isCompanyHub,
			isTargetAudience,
			isSegmentationApplied,
			setIsSegmentationApplied,
		}),
		[currentAudienceGroupIds, isCompanyHub, isTargetAudience, isSegmentationApplied],
	);

	const { userId } = useSessionData();

	const { error: queryError, loading: queryLoading } = useQueryInterop<
		AudienceSegmentationContextProviderQuery$data,
		AudienceSegmentationContextProviderQuery$variables
	>(AudienceSegmentationContextProviderQuery, {
		skip: !shouldFetchUserGroups || !isCompanyHub,
		variables: {
			accountId: userId,
		},
		onCompleted: (data) => {
			const userGroupIds: string[] =
				data?.user?.confluence?.groupsWithId
					?.map((result) => result?.id)
					.filter((id): id is string => !!id) ?? [];

			// onCompleted shouldn't be called if we skip the query but this is to ensure
			// the state doesn't get set when the fixture mock gives this query the default value
			shouldFetchUserGroups && setCurrentAudienceGroupIds(userGroupIds);
		},
	});

	const errorDisplay = useMemo(() => {
		if (queryError && !queryLoading) {
			if (isUnauthorizedError(queryError)) {
				markErrorAsHandled(queryError);
			} else {
				return <ErrorDisplay error={queryError} />;
			}
		}
		return null;
	}, [queryError, queryLoading]);

	return (
		<AudienceSegmentationContext.Provider value={audienceSegmentationContextValue}>
			{errorDisplay}
			{children}
		</AudienceSegmentationContext.Provider>
	);
};

export const AudienceSegmentationContextProvider = withErrorBoundary({
	attribution: Attribution.COMPANY_HUB,
})(AudienceSegmentationContextProviderInternal);
